/** Use as: 
  <custom-video class="video-element video-element--landscape video--cover" data-options='{
    "autoplay": {{ video_autoplay }}
  }'>
      <div class="video-section__placeholder" data-video-trigger>
         <img src=""> // placeholder image
        <button class="video-element__button button-reset" aria-label="{%- render 'format-translation', namespace: 'products.product.media', key: 'play_video', fallback: 'Play video' -%}" data-video-play-button>
          {%- render 'global-icon', icon: 'play_button', icon_size: 'md' -%}
        </button>
      </div>
      <video {% if video_placeholder != blank %}poster="{{ video_placeholder | img_url: 'master' }}"{% endif %} preload muted playsinline> 
        <source src="{{- video_url -}}" type="video/mp4" />
      </video>
    </div>
  </custom-video>
*/

if (!customElements.get("custom-video")) {
  class CustomVideo extends HTMLElement {
    constructor() {
      super();

      // Get options from element data and combine with self.options
      if (this?.dataset?.options) {
        this.options = JSON.parse(this.dataset.options);
      }

      this.links = this.querySelectorAll("a");
      this.video = this.querySelector("video");
      this.videoTriggers = this.querySelectorAll("[data-video-trigger]");
      this.videoWrapper = this.closest("[data-video-wrapper]");
      this.overlayImage = this.videoWrapper.querySelector("[data-video-overlay-image]");
      this.overlayMobImage = this.videoWrapper.querySelector("[data-video-overlay-image-mobile]");
      this.overlayText = this.videoWrapper.querySelector("[data-video-text-overlay]");

      // video and button click
      this.videoTriggers?.forEach((trigger) =>
        trigger.addEventListener("click", this.loadVideo.bind(this)),
      );
      this.video.addEventListener("click", this.loadVideo.bind(this));

      // catch click on links that should not trigger the video
      this.links?.forEach((link) =>
        link.addEventListener("click", function (event) {
          event.stopImmediatePropagation();
        }),
      );

      this.breakpoints = breakpoints();
      this.breakPointName = this.breakpoints[this.options.video_breakpoint][0];
      this.breakPointSize = this.breakpoints[this.options.video_breakpoint][1];

      this.debouncedOnLoad = debounce((event) => {
        this.videoObserver(event);
      }, 100);

      // when autoplay is enabled, check if the video is in viewport and play it (if it's not in a slider)
      if (this.options.autoplay) {
        // pause autoplay in slider and let videoObserver determine if it should play
        if (this.closest(".swiper-slide") !== null) {
          this.pauseVideo();
        }

        // play video on scroll
        window.addEventListener("scroll", this.debouncedOnLoad.bind(this));

        // play video if it's visible on load
        window.addEventListener("load", this.debouncedOnLoad.bind(this));

        // play video if it's visible on load
        window.addEventListener("resize", this.debouncedOnLoad.bind(this));
      }
    }

    videoObserver(event) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const elementBounds = entry.boundingClientRect;
          const isSlideLeft = !!elementBounds.left == 0;
          const isBottomVisible =
            !!(elementBounds.bottom < window.innerHeight) && elementBounds.bottom;
          const isTopVisible = !!(elementBounds.top > 0) && elementBounds.top;

          if (!this.video.paused) {
            this.pauseVideo();
          }

          // only load videos for the correct breakpoint so responsive video's are not loaded simultaneously
          if (this.options.mp4_desktop && this.options.mp4_mobile) {
            // only 1 video for all screens
            this.observedVideoCallback(entry.target, isBottomVisible, isTopVisible, isSlideLeft);
          } else if (
            this.options.mp4_desktop &&
            !this.options.mp4_mobile &&
            window.innerWidth >= this.breakPointSize &&
            this.parentNode.classList.contains(`visibility-${this.breakPointName}--visible`)
          ) {
            // 2 videos exist and view is desktop
            this.observedVideoCallback(entry.target, isBottomVisible, isTopVisible, isSlideLeft);
          } else if (
            !this.options.mp4_desktop &&
            this.options.mp4_mobile &&
            window.innerWidth < this.breakPointSize &&
            this.parentNode.classList.contains(`visibility-${this.breakPointName}--hidden`)
          ) {
            // 2 videos exist and view is mobile
            this.observedVideoCallback(entry.target, isBottomVisible, isTopVisible, isSlideLeft);
          }
        });
      });

      if (this.video.dataset.videoIsPaused !== "true") {
        observer.observe(this.video);
      }
    }

    observedVideoCallback(target, isBottomVisible, isTopVisible, isSlideLeft) {
      if (target.closest(".swiper-slide") !== null) {
        if (isBottomVisible && isTopVisible && isSlideLeft) {
          this.playVideo();
        }
      } else if (isBottomVisible && isTopVisible) {
        this.playVideo();
      } else {
        this.pauseVideo();
      }
    }

    // when video is manually paused, add data attribute to keep track of paused state
    loadVideo(event) {
      event.preventDefault();
      event.stopPropagation();

      if (this.video.paused) {
        this.playVideo();
        this.video.dataset.videoIsPaused = "false";
      } else {
        this.pauseVideo();
        this.video.dataset.videoIsPaused = "true";
      }
    }

    playVideo() {
      if (window.innerWidth <= parseInt(this.overlayMobImage?.dataset.bp)) {
        this.overlayMobImage?.classList.add("hidden");
      }
      this.overlayImage?.classList.add("hidden");
      this.overlayText?.classList.add("hidden");
      this.videoTriggers[0]?.classList.add("hidden");
      this.video.play();
    }

    pauseVideo() {
      if (window.innerWidth <= parseInt(this.overlayMobImage?.dataset.bp)) {
        this.overlayMobImage?.classList.remove("hidden");
      }
      this.overlayImage?.classList.remove("hidden");
      this.overlayText?.classList.remove("hidden");
      this.videoTriggers[0]?.classList.remove("hidden");
      this.video.pause();
    }
  }

  window.CustomVideo = CustomVideo;
  customElements.define("custom-video", CustomVideo);
}
